import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "@emotion/styled";
import { Input, Tree, TreeDataNode } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Divider from "components/ui/divider/Divider";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useDebouncedValue } from "lib/debounce";
import {
  CustomerDocumentDetails,
  useCustomerDocuments,
  useInfiniteCustomerDocuments,
} from "lib/hooks/api/customer_documents/useCustomerDocuments";
import useParagonUser from "lib/hooks/api/integrations/useParagonUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentStatus,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import { isDefined, pluralise } from "lib/utils";

const SELECT_ALL_CONTENT_LIBRARY_KEY = "SELECT_ALL_CONTENT_LIBRARY";
const SELECT_ALL_GOOGLE_DRIVE_KEY = "SELECT_ALL_GOOGLE_DRIVE";
const SELECT_ALL_SHAREPOINT_KEY = "SELECT_ALL_SHAREPOINT";

type OriginSource = "googledrive" | "sharepoint";
type SourceKey = "uploads" | OriginSource;

const DUMMY_SELECT_ALL_DOCUMENT: CustomerDocumentDetails = {
  isFolder: true,
  id: SELECT_ALL_CONTENT_LIBRARY_KEY,
  fileName: "Content Library",
  uiPath: null,
  status: CustomerDocumentStatus.Success,
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

const DUMMY_GOOGLE_DRIVE_DOCUMENT: CustomerDocumentDetails = {
  isFolder: true,
  id: SELECT_ALL_GOOGLE_DRIVE_KEY,
  fileName: "Google Drive",
  uiPath: null,
  status: CustomerDocumentStatus.Success,
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

const DUMMY_SHAREPOINT_DOCUMENT: CustomerDocumentDetails = {
  isFolder: true,
  id: SELECT_ALL_SHAREPOINT_KEY,
  fileName: "SharePoint",
  uiPath: null,
  status: CustomerDocumentStatus.Success,
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

/**
 * Helper flattens all docs in the current tree
 * @param nodes
 * @returns
 */
function collectAllDocs(nodes: CustomTreeNode[]): CustomerDocumentDetails[] {
  const docs: CustomerDocumentDetails[] = [];
  const walk = (arr: CustomTreeNode[]) => {
    arr.forEach((n) => {
      if (n.document) {
        docs.push(n.document);
      }
      if (n.children) walk(n.children as CustomTreeNode[]);
    });
  };
  walk(nodes);
  return docs;
}

type Props<T extends FieldValues> = {
  name: Path<T>;
  title?: ReactNode;
  control: Control<T, unknown>;
  documentType: CustomerDocumentTypeFilter;
  checkable: boolean;
  onlyFetchFolders?: boolean;
  excludeDocuments?: string[]; // IDs of documents to exclude from the list
  onSelect?: (selectedDocuments: CustomerDocumentDetails[]) => void;
  inputPlaceholder?: string;
  showFlatSearch?: boolean;
};

type CustomTreeNode = {
  document?: CustomerDocumentDetails;
  originSource?: OriginSource; // undefined = content library
} & TreeDataNode;

type TreeMap = Record<SourceKey, CustomTreeNode[]>;

export function CustomerDocumentSelector<T extends FieldValues>({
  name,
  control,
  documentType,
  title,
  inputPlaceholder = "Search",
  checkable,
  onlyFetchFolders,
  excludeDocuments,
  onSelect,
  showFlatSearch,
}: Props<T>) {
  const { user } = useParagonUser();

  const isGoogleDriveEnabled = !!user?.integrations?.googledrive?.enabled;
  const isSharePointEnabled = !!user?.integrations?.sharepoint?.enabled;

  const [activeRoot, setActiveRoot] = useState<SourceKey>("uploads");
  const [expandedParentNode, setExpandedParentNode] = useState<CustomTreeNode | null>(null);

  const [trees, setTrees] = useState<TreeMap>({
    uploads: [],
    googledrive: [],
    sharepoint: [],
  });

  const [textSearch, setTextSearch] = useState("");
  const [debouncedTextSearch, setDebouncedTextValue] = useDebouncedValue(textSearch, 400);

  useEffect(() => {
    const isGoogleDriveVisible = !showFlatSearch && isGoogleDriveEnabled;
    const isSharePointVisible = !showFlatSearch && isSharePointEnabled;

    if (activeRoot === "googledrive" && !isGoogleDriveVisible) setActiveRoot("uploads");
    if (activeRoot === "sharepoint" && !isSharePointVisible) setActiveRoot("uploads");
  }, [activeRoot, isGoogleDriveEnabled, isSharePointEnabled, showFlatSearch]);

  const commonInfiniteQueryParams = {
    documentType: documentType,
    onlyFetchFolders,
    excludeDocuments,
    textSearch: debouncedTextSearch,
    sortBy: CustomerDocumentSortBy.FolderFirst,
    sortOrder: CustomerDocumentSortOrder.Asc,
  };

  // Fetch top-level documents
  const uploadsQuery = useInfiniteCustomerDocuments(commonInfiniteQueryParams, {
    page: 1,
    pageSize: 100,
  });

  // Google Drive root
  const googleQuery = useInfiniteCustomerDocuments(
    {
      ...commonInfiniteQueryParams,
      originSource: "googledrive",
    },
    { page: 1, pageSize: 100 },
  );

  // SharePoint root
  const sharepointQuery = useInfiniteCustomerDocuments(
    {
      ...commonInfiniteQueryParams,
      originSource: "sharepoint",
    },
    { page: 1, pageSize: 100 },
  );

  const loadedUploadsDocs = useMemo(
    () => uploadsQuery.data?.pages.flatMap((p) => p.customerDocuments.items) || [],
    [uploadsQuery.data],
  );
  const loadedGoogleDocs = useMemo(
    () => googleQuery.data?.pages.flatMap((p) => p.customerDocuments.items) || [],
    [googleQuery.data],
  );
  const loadedSharepointDocs = useMemo(
    () => sharepointQuery.data?.pages.flatMap((p) => p.customerDocuments.items) || [],
    [sharepointQuery.data],
  );

  // Fetch child documents on demand, i.e. when expanding a node
  const { data: childDocuments } = useCustomerDocuments(
    {
      documentType: documentType,
      uiPath: expandedParentNode?.document?.uiPath,
      onlyFetchFolders,
      excludeDocuments,
      textSearch: debouncedTextSearch,
      sortBy: CustomerDocumentSortBy.FolderFirst,
      sortOrder: CustomerDocumentSortOrder.Asc,
      originSource: expandedParentNode?.originSource,
    },
    // No pagination for child documents for now; can revisit if it becomes an issue
    {
      page: undefined,
      pageSize: undefined,
    },
    { enabled: !!expandedParentNode?.document?.uiPath },
  );

  useEffect(() => {
    setTrees((prev) => ({
      ...prev,
      uploads: mergeRootDocuments(prev.uploads, loadedUploadsDocs, undefined),
    }));
  }, [loadedUploadsDocs]);

  useEffect(() => {
    if (showFlatSearch || !isGoogleDriveEnabled) return;
    setTrees((prev) => ({
      ...prev,
      googledrive: mergeRootDocuments(prev.googledrive, loadedGoogleDocs, "googledrive"),
    }));
  }, [showFlatSearch, isGoogleDriveEnabled, loadedGoogleDocs]);

  useEffect(() => {
    if (showFlatSearch || !isSharePointEnabled) return;
    setTrees((prev) => ({
      ...prev,
      sharepoint: mergeRootDocuments(prev.sharepoint, loadedSharepointDocs, "sharepoint"),
    }));
  }, [showFlatSearch, isSharePointEnabled, loadedSharepointDocs]);

  useEffect(() => {
    if (!expandedParentNode || !childDocuments?.length) return;
    if (expandedParentNode.children && expandedParentNode.children.length > 0) return;

    const source: SourceKey = expandedParentNode.originSource ?? "uploads";
    const childTreeNodes = mapDocumentsToTreeData(childDocuments, expandedParentNode.originSource);

    const addChildren = (nodes: CustomTreeNode[]): CustomTreeNode[] =>
      nodes.map((node) => {
        if (node.key === expandedParentNode.key) {
          return {
            ...node,
            children: childTreeNodes,
            isLeaf: childTreeNodes.length === 0,
          };
        }
        if (node.children) {
          return { ...node, children: addChildren(node.children) };
        }
        return node;
      });

    setTrees((prev) => ({
      ...prev,
      [source]: addChildren(prev[source]),
    }));
  }, [childDocuments, expandedParentNode]);

  const activePaging = useMemo(() => {
    if (activeRoot === "googledrive") {
      return {
        dataLength: loadedGoogleDocs.length,
        next: googleQuery.fetchNextPage,
        hasMore: googleQuery.hasNextPage || false,
      };
    }
    if (activeRoot === "sharepoint") {
      return {
        dataLength: loadedSharepointDocs.length,
        next: sharepointQuery.fetchNextPage,
        hasMore: sharepointQuery.hasNextPage || false,
      };
    }
    return {
      dataLength: loadedUploadsDocs.length,
      next: uploadsQuery.fetchNextPage,
      hasMore: uploadsQuery.hasNextPage || false,
    };
  }, [
    activeRoot,
    loadedGoogleDocs.length,
    loadedSharepointDocs.length,
    loadedUploadsDocs.length,
    googleQuery.fetchNextPage,
    googleQuery.hasNextPage,
    sharepointQuery.fetchNextPage,
    sharepointQuery.hasNextPage,
    uploadsQuery.fetchNextPage,
    uploadsQuery.hasNextPage,
  ]);

  const totalResults = uploadsQuery.data?.pages[0]?.customerDocuments.totalCount || 0;

  const treeInitialLoading =
    uploadsQuery.isInitialLoading ||
    (!showFlatSearch &&
      ((isGoogleDriveEnabled && googleQuery.isInitialLoading) ||
        (isSharePointEnabled && sharepointQuery.isInitialLoading)));

  const rootTreeData: CustomTreeNode[] = useMemo(() => {
    const roots: CustomTreeNode[] = [
      {
        key: SELECT_ALL_CONTENT_LIBRARY_KEY,
        title: "Content Library",
        isLeaf: false,
        selectable: true,
        checkable: checkable,
        document: DUMMY_SELECT_ALL_DOCUMENT,
        children: trees.uploads,
      },
    ];

    if (!showFlatSearch) {
      if (isGoogleDriveEnabled) {
        roots.push({
          key: SELECT_ALL_GOOGLE_DRIVE_KEY,
          title: "Google Drive",
          isLeaf: false,
          selectable: true,
          checkable: checkable,
          originSource: "googledrive",
          document: DUMMY_GOOGLE_DRIVE_DOCUMENT,
          children: trees.googledrive,
        });
      }

      if (isSharePointEnabled) {
        roots.push({
          key: SELECT_ALL_SHAREPOINT_KEY,
          title: "SharePoint",
          isLeaf: false,
          selectable: true,
          checkable: checkable,
          originSource: "sharepoint",
          document: DUMMY_SHAREPOINT_DOCUMENT,
          children: trees.sharepoint,
        });
      }
    }

    return roots;
  }, [
    checkable,
    isGoogleDriveEnabled,
    isSharePointEnabled,
    showFlatSearch,
    trees.googledrive,
    trees.sharepoint,
    trees.uploads,
  ]);

  const allIdsByRootKey = useMemo(() => {
    return {
      [SELECT_ALL_CONTENT_LIBRARY_KEY]: collectAllDocs(trees.uploads).map((d) => d.id),
      [SELECT_ALL_GOOGLE_DRIVE_KEY]: collectAllDocs(trees.googledrive).map((d) => d.id),
      [SELECT_ALL_SHAREPOINT_KEY]: collectAllDocs(trees.sharepoint).map((d) => d.id),
    } as Record<string, string[]>;
  }, [trees.uploads, trees.googledrive, trees.sharepoint]);

  return (
    <Container>
      <Header>
        {title}
        {showFlatSearch && (
          <Input
            placeholder={inputPlaceholder}
            onChange={(e) => setTextSearch(e.currentTarget.value.trim())}
            value={textSearch}
            prefix={<UIcon icon="searchSm" size={16} color="sysPrimaryDefault" />}
          />
        )}
      </Header>
      <Divider margin="0" />
      <ScrollContainer id="scrollableDiv">
        <InfiniteScroll
          dataLength={activePaging.dataLength}
          next={activePaging.next}
          hasMore={activePaging.hasMore}
          loader={<CentredSpinner />}
          scrollableTarget="scrollableDiv"
          style={{ height: "inherit", overflow: "inherit" }}
        >
          {treeInitialLoading ? (
            <Flex gap={8} flexDirection="column">
              <SkeletonText width="100%" />
              <SkeletonText width="100%" />
              <SkeletonText width="100%" />
            </Flex>
          ) : (
            <Controller
              control={control}
              name={name}
              render={({ field }) => {
                return showFlatSearch && debouncedTextSearch ? (
                  <Flex column>
                    <Total description>{pluralise(totalResults, "Result")}</Total>
                    {loadedUploadsDocs.map((document) => {
                      const doc = {
                        ...document,
                        uiPath: getFullPath(document.fileName, document.uiPath ?? ""),
                      };
                      return (
                        <Item
                          key={document.id}
                          onClick={() => {
                            if (!onSelect) return;
                            onSelect([doc]);
                          }}
                        >
                          <UIcon icon={document.isFolder ? "folder" : "file06"} size={16} />
                          <Flex column gap={4}>
                            <Text>{document.fileName}</Text>
                            <Text color="sysTextSecondary" description>
                              {document.uiPath ? formatPath(document.uiPath) : ""}
                            </Text>
                          </Flex>
                        </Item>
                      );
                    })}
                  </Flex>
                ) : (
                  <StyledTree
                    {...field}
                    defaultExpandedKeys={[SELECT_ALL_CONTENT_LIBRARY_KEY]}
                    treeData={rootTreeData}
                    switcherIcon={
                      <div>
                        <UIcon icon="chevronDown" size={16} />
                      </div>
                    }
                    checkable={checkable}
                    checkedKeys={field.value}
                    onSelect={(_keys, info) => {
                      if (!onSelect) return;

                      const nodeKey = info.node.key.toString();

                      if (nodeKey === SELECT_ALL_CONTENT_LIBRARY_KEY) {
                        onSelect([DUMMY_SELECT_ALL_DOCUMENT]);
                      } else if (nodeKey === SELECT_ALL_GOOGLE_DRIVE_KEY) {
                        onSelect([DUMMY_GOOGLE_DRIVE_DOCUMENT]);
                      } else if (nodeKey === SELECT_ALL_SHAREPOINT_KEY) {
                        onSelect([DUMMY_SHAREPOINT_DOCUMENT]);
                      } else {
                        const selectedDocs = info.selectedNodes
                          .map((n) => (n as CustomTreeNode).document)
                          .filter(isDefined);

                        onSelect(selectedDocs);
                      }
                      setDebouncedTextValue("");
                    }}
                    onCheck={(checkedKeys) => {
                      const current = Array.isArray(checkedKeys)
                        ? checkedKeys
                        : checkedKeys.checked;
                      const keySet = new Set(current.map(String));

                      const expandRoot = (rootKey: string) => {
                        if (!keySet.has(rootKey)) return;
                        keySet.delete(rootKey);

                        for (const id of allIdsByRootKey[rootKey] ?? []) {
                          keySet.add(id);
                        }
                      };

                      expandRoot(SELECT_ALL_CONTENT_LIBRARY_KEY);
                      expandRoot(SELECT_ALL_GOOGLE_DRIVE_KEY);
                      expandRoot(SELECT_ALL_SHAREPOINT_KEY);

                      field.onChange(Array.from(keySet));
                    }}
                    onExpand={(_expandedKeys, info) => {
                      const node = info.node as CustomTreeNode;
                      const nodeKey = node.key?.toString();

                      // Set active root whenever user expands within that subtree
                      if (info.expanded) {
                        if (nodeKey === SELECT_ALL_GOOGLE_DRIVE_KEY) setActiveRoot("googledrive");
                        else if (nodeKey === SELECT_ALL_SHAREPOINT_KEY) setActiveRoot("sharepoint");
                        else if (nodeKey === SELECT_ALL_CONTENT_LIBRARY_KEY)
                          setActiveRoot("uploads");
                        else setActiveRoot(node.originSource ?? "uploads");
                      }

                      // Only fetch children for real folder nodes with a uiPath
                      if (info.expanded && node.document?.isFolder && node.document.uiPath) {
                        setExpandedParentNode(node);
                      } else {
                        setExpandedParentNode(null);
                      }
                    }}
                    titleRender={(node) => {
                      const item = node as CustomTreeNode;
                      return (
                        <ItemContainer>
                          <UIcon icon={item.document?.isFolder ? "folder" : "file06"} size={16} />
                          <Text ellipsis ellipsisMethod="lineClamp">
                            {item.document?.fileName}
                          </Text>
                        </ItemContainer>
                      );
                    }}
                  />
                );
              }}
            />
          )}
        </InfiniteScroll>
      </ScrollContainer>
    </Container>
  );
}

function formatPath(uiPath: string) {
  const pathParts = uiPath.split("/");
  return pathParts.join(" / ");
}
function getFullPath(fileName: string, uiPath?: string) {
  return uiPath ? `${uiPath}/${fileName}` : fileName;
}

function mapDocumentsToTreeData(
  documents: CustomerDocumentDetails[],
  originSource?: OriginSource,
): CustomTreeNode[] {
  return documents.map((document) => {
    return {
      key: document.id,
      title: document.fileName,
      isLeaf: !document.isFolder,
      originSource,
      document: {
        ...document,
        uiPath: getFullPath(document.fileName, document.uiPath ?? ""),
      },
    };
  });
}

function mergeRootDocuments(
  prevNodes: CustomTreeNode[],
  nextDocs: CustomerDocumentDetails[],
  originSource?: OriginSource,
): CustomTreeNode[] {
  const prevById = new Map<string, CustomTreeNode>();
  prevNodes.forEach((n) => {
    prevById.set(String(n.key), n);
  });

  const nextNodes = mapDocumentsToTreeData(nextDocs, originSource);

  return nextNodes.map((n) => {
    const existing = prevById.get(String(n.key));
    if (!existing) return n;

    return {
      ...existing,
      ...n,
      children: existing.children,
      isLeaf: n.isLeaf,
    };
  });
}

const Item = styled(Flex)({
  cursor: "pointer",
  padding: 8,
  gap: 8,
  alignItems: "center",
});

const Container = styled(Flex)({
  flexDirection: "column",
  width: 480,
  maxHeight: 480,
  minHeight: 100,
});

const ScrollContainer = styled(Flex)({
  flex: 1,
  overflowY: "auto",
  flexDirection: "column",
  padding: 16,
  paddingBottom: 8,
});

const Header = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  padding: 16,
});

const ItemContainer = styled(Flex)({
  gap: 8,
  alignItems: "center",
});

const Total = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  padding: 8,
}));

// Ant doesn’t expose direct props for styling tree nodes,
// so we override its internal class names here.
// Because this uses Emotion’s styled(), the overrides
// are scoped to this Tree instance only
const StyledTree = styled(Tree)<{ treeData: CustomTreeNode[] }>({
  "& .ant5-tree-treenode": {
    padding: "8px",
  },

  "& .ant5-tree-switcher-icon": {
    verticalAlign: "text-top !important",
  },
});

export default CustomerDocumentSelector;
